import React from 'react';
import { Link } from 'react-router-dom';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { Counter } from '@/components/site/Counter';
import { HeroImage } from '@/components/site/HeroImage';
import { SITE, SERVICES } from '@/data/site';

export const H2 = 'font-display font-semibold text-[clamp(36px,5vw,80px)] leading-[0.98] tracking-[-0.045em] text-balance';
export const H2_SM = 'font-display font-semibold text-[clamp(32px,3.6vw,56px)] leading-[1] tracking-[-0.04em] text-balance';
export const SECTION = 'relative py-[clamp(96px,12vw,180px)] border-t border-white/[0.08]';
export const WRAP = 'max-w-7xl mx-auto px-4 sm:px-6 md:px-12';

/** Splits `heading` around `accentWord` and renders the accent in the serif. */
export function Accented({ text, accent }) {
  const i = accent ? text.indexOf(accent) : -1;
  if (i < 0) return text;
  return (<>{text.slice(0, i)}<span className="nd-accent">{accent}</span>{text.slice(i + accent.length)}</>);
}

/** Standard two-column section head: mono label left, display heading right. */
export function SectionHead({ num, label, heading, accent, intro, aside, className = '' }) {
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-14 md:mb-20 ${className}`}>
      <SectionLabel num={num} className="lg:pt-4">{label}</SectionLabel>
      <div>
        <Reveal>
          <h2 className={`${H2} max-w-[20ch]`}><Accented text={heading} accent={accent} /></h2>
        </Reveal>
        {(intro || aside) && (
          <Reveal delay={100} className="mt-7 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            {intro && <p className="text-ink-muted text-[17px] leading-relaxed max-w-[56ch]">{intro}</p>}
            {aside}
          </Reveal>
        )}
      </div>
    </div>
  );
}

/**
 * Shared hero block for every service detail page.
 */
export function ServiceHero({
  serviceNum,
  serviceTitle,
  headlineText,
  accent,
  description,
  primaryCta = { label: 'Book discovery call', href: SITE.whatsapp },
  secondaryCta,
  testIdPrefix,
  image,
}) {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
        style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.32), rgba(75,159,255,.12) 40%, transparent 68%)' }}
      />
      <div className={`relative ${WRAP} pt-16 md:pt-24 pb-20 md:pb-28`}>
        <nav data-testid={`${testIdPrefix}-breadcrumb`} aria-label="Breadcrumb" className="nd-mono flex flex-wrap items-center gap-2.5 !text-[11px]">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span aria-hidden>/</span>
          <Link to="/services/" className="hover:text-white transition-colors">Services</Link>
          <span aria-hidden>/</span>
          <span className="text-white">{serviceTitle}</span>
        </nav>

        <SectionLabel as="h1" num={serviceNum} data-testid={`${testIdPrefix}-eyebrow`} className="mt-12 md:mt-16">
          {SERVICES.find((s) => s.num === serviceNum)?.h1 || serviceTitle}
        </SectionLabel>
        <WordReveal
          as="p"
          text={headlineText}
          accent={accent}
          data-testid={`${testIdPrefix}-headline`}
          className="mt-8 max-w-[15ch] font-display font-semibold text-[clamp(44px,7.4vw,128px)] leading-[0.92] tracking-[-0.05em]"
        />

        <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {image && (
            <div className="lg:col-span-5" data-testid={`${testIdPrefix}-hero-image`}>
              <HeroImage src={image.src} alt={image.alt} testId={`${testIdPrefix}-hero-image-frame`} />
            </div>
          )}
          <div className={image ? 'lg:col-span-6 lg:col-start-7' : 'lg:col-span-7 lg:col-start-6'}>
            <Reveal delay={350}>
              <p className="text-lg md:text-xl text-[#C9C9D3] leading-relaxed max-w-[46ch]">{description}</p>
            </Reveal>
            <Reveal delay={450}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <MagneticButton href={primaryCta.href} external variant="primary" data-testid={`${testIdPrefix}-hero-cta-primary`}>
                  {primaryCta.label}
                </MagneticButton>
                {secondaryCta && (
                  <MagneticButton to={secondaryCta.to} variant="ghost" data-testid={`${testIdPrefix}-hero-cta-secondary`}>
                    {secondaryCta.label}
                  </MagneticButton>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Text-link row to the location, cost and sub-service pages that belong to a
 * service. Descriptive anchors: this is the main internal-link path to them.
 */
export function PageLinks({ heading, intro, links, testIdPrefix }) {
  return (
    <section data-testid={`${testIdPrefix}-page-links`} className="relative py-20 md:py-28 border-t border-white/[0.08]">
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-10`}>
        <div className="lg:col-span-5">
          <h2 className={H2_SM}>{heading}</h2>
          {intro && <p className="mt-5 text-ink-muted leading-relaxed max-w-md">{intro}</p>}
        </div>
        <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 border-t border-white/[0.08] self-start">
          {links.map((l) => (
            <li key={l.to} className="border-b border-white/[0.08]">
              <Link to={l.to} className="arrow-link group flex items-center justify-between gap-4 py-4 pr-4 text-[#C9C9D3] hover:text-white transition-colors">
                {l.label} <span className="arrow text-ink-muted group-hover:text-white">→</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * Related services list that excludes the current page's slug.
 */
export function RelatedServices({ exclude, testIdPrefix, num = '09' }) {
  const related = SERVICES.filter((s) => s.slug !== exclude).slice(0, 3);
  return (
    <section data-testid={`${testIdPrefix}-related`} className={SECTION}>
      <div className={WRAP}>
        <SectionHead
          num={num}
          label="Related services"
          heading="If this fits, these might too."
          accent="might too."
          aside={<Link to="/services/" className="nd-link-u text-sm text-white whitespace-nowrap">All services →</Link>}
        />
        <ul className="border-t border-white/[0.08]">
          {related.map((s, i) => (
            <Reveal as="li" key={s.slug} delay={i * 60} className="border-b border-white/[0.08]">
              <Link
                to={`/services/${s.slug}/`}
                data-testid={`${testIdPrefix}-related-${s.slug}`}
                className="group relative isolate grid grid-cols-[1fr_auto] lg:grid-cols-[220px_1fr_auto] gap-x-8 gap-y-3 items-center py-8 md:py-10"
              >
                <span aria-hidden className="absolute inset-y-0 -inset-x-4 md:-inset-x-12 -z-10 origin-bottom scale-y-0 bg-[linear-gradient(90deg,rgba(108,92,231,.14),rgba(75,159,255,.04))] transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:scale-y-100" />
                <span className="nd-mono col-span-2 lg:col-span-1">{s.num} / 06</span>
                <span className="font-display font-semibold text-[clamp(28px,3.6vw,56px)] tracking-[-0.045em] leading-none transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:translate-x-4">
                  {s.title}
                </span>
                <span aria-hidden className="h-12 w-12 md:h-14 md:w-14 rounded-full border border-white/15 grid place-items-center transition-colors duration-300 group-hover:bg-white group-hover:text-ink-bg group-hover:border-white">
                  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" className="transition-transform duration-500 group-hover:-rotate-45"><path d="M2 8h12M9 3l5 5-5 5" /></svg>
                </span>
                <p className="col-span-2 lg:col-start-2 lg:col-span-1 text-ink-muted text-[15px] max-w-[60ch]">{s.short}</p>
              </Link>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * Problems-we-solve block: numbered rows beside a sticky heading.
 * items: [{icon, title, body, slug}]
 */
export function ProblemsBlock({ heading, accentWord, intro, items, testIdPrefix }) {
  return (
    <section data-testid={`${testIdPrefix}-problems`} className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <SectionLabel num="02">Problems we solve</SectionLabel>
            <Reveal>
              <h2 className={`mt-7 ${H2_SM}`}><Accented text={heading} accent={accentWord} /></h2>
            </Reveal>
            {intro && <Reveal delay={100}><p className="mt-6 text-ink-muted leading-relaxed max-w-md">{intro}</p></Reveal>}
          </div>
        </div>
        <ol className="lg:col-span-7 border-t border-white/[0.08]">
          {items.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal as="li" key={p.title} delay={i * 60} className="border-b border-white/[0.08]">
                <div data-testid={`${testIdPrefix}-problem-${p.slug}`} className="grid grid-cols-[56px_1fr] md:grid-cols-[72px_1fr_auto] gap-x-6 gap-y-3 py-8 md:py-10">
                  <span className="nd-mono pt-2">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="font-display text-2xl md:text-[32px] font-semibold tracking-[-0.035em] leading-tight">{p.title}</h3>
                    <p className="mt-3 text-ink-muted leading-relaxed max-w-[54ch]">{p.body}</p>
                  </div>
                  {Icon && (
                    <span aria-hidden className="hidden md:grid h-12 w-12 rounded-full border border-white/10 place-items-center text-[#8C7CFF]">
                      <Icon size={18} strokeWidth={1.5} />
                    </span>
                  )}
                </div>
              </Reveal>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

/**
 * "What we build" grid: six tiles with a spotlight that follows the cursor.
 * items: [{tag, title, body, accent, slug}]
 */
export function BuildBlock({ heading, accentWord, intro, items, testIdPrefix }) {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  return (
    <section data-testid={`${testIdPrefix}-what-we-build`} className={SECTION}>
      <div className={WRAP}>
        <SectionHead num="03" label="What we build" heading={heading} accent={accentWord} intro={intro} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.08] rounded-[18px] overflow-hidden ring-1 ring-white/[0.08]">
          {items.map((w, i) => (
            <Reveal key={w.title} delay={(i % 3) * 70} className="bg-ink-bg">
              <div
                onMouseMove={onMove}
                data-testid={`${testIdPrefix}-build-${w.slug}`}
                className="group relative h-full min-h-[280px] p-8 md:p-10 flex flex-col overflow-hidden"
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: 'radial-gradient(420px circle at var(--mx,50%) var(--my,50%), rgba(108,92,231,.16), transparent 60%)' }}
                />
                <div className="relative flex items-center justify-between">
                  <span className="nd-mono !text-[11px]">{w.tag}</span>
                  <span className="nd-mono !text-[11px]">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="relative mt-auto pt-16 font-display text-[28px] md:text-[34px] font-semibold tracking-[-0.04em] leading-none">{w.title}</h3>
                <p className="relative mt-4 text-ink-muted leading-relaxed text-[15px] max-w-md">{w.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Process / approach: stacking sticky cards.
 */
export function ApproachBlock({ heading, accentWord, intro, steps, testIdPrefix, num = '04' }) {
  return (
    <section data-testid={`${testIdPrefix}-approach`} className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <SectionLabel num={num}>Our approach</SectionLabel>
            <Reveal><h2 className={`mt-7 ${H2_SM}`}><Accented text={heading} accent={accentWord} /></h2></Reveal>
            {intro && <Reveal delay={100}><p className="mt-6 text-ink-muted leading-relaxed max-w-sm">{intro}</p></Reveal>}
          </div>
        </div>
        <ol className="lg:col-span-8 lg:col-start-5">
          {steps.map((p, i) => (
            <li
              key={p.step}
              data-testid={`${testIdPrefix}-process-${p.slug}`}
              className="md:sticky mb-4 rounded-[18px] border border-white/[0.08] bg-ink-surface p-7 md:p-10 shadow-[0_-24px_48px_-24px_rgba(5,5,12,.95)]"
              style={{ top: `${120 + i * 18}px` }}
            >
              <div className="flex items-start justify-between gap-6">
                <h3 className="font-display text-2xl md:text-[34px] font-semibold tracking-[-0.035em] leading-tight">{p.title}</h3>
                <span className="font-display font-medium text-[56px] md:text-[88px] leading-[0.8] tracking-[-0.06em] text-white/[0.12] tabular-nums">{p.step}</span>
              </div>
              <p className="mt-6 text-ink-muted leading-relaxed max-w-2xl">{p.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/**
 * Counters row. values: [{kpi, prefix, suffix, label, decimals}]
 */
export function OutcomesBlock({ heading, accentWord, intro, values, testIdPrefix, num = '07' }) {
  return (
    <section data-testid={`${testIdPrefix}-outcomes`} className={SECTION}>
      <div className={WRAP}>
        <SectionHead num={num} label="Outcomes" heading={heading} accent={accentWord} intro={intro} />
        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-white/[0.08]">
          {values.map((o, i) => (
            <Reveal key={o.label} delay={i * 80}>
              <div data-testid={`${testIdPrefix}-outcome-${i}`} className={`pt-8 pb-4 pr-4 ${i % 2 ? 'pl-5 border-l' : ''} ${i > 1 ? 'lg:pl-5 lg:border-l' : ''} ${i === 2 ? 'max-lg:pl-0 max-lg:border-l-0 max-lg:border-t max-lg:mt-6' : ''} ${i === 3 ? 'max-lg:border-t max-lg:mt-6' : ''} border-white/[0.08]`}>
                <div className="font-display font-medium text-[clamp(48px,6vw,96px)] tracking-[-0.055em] leading-none">
                  <Counter value={o.kpi} prefix={o.prefix || ''} suffix={o.suffix || ''} decimals={o.decimals || (!Number.isInteger(o.kpi) ? 1 : 0)} />
                </div>
                <div className="mt-4 text-sm text-ink-muted leading-snug max-w-[24ch]">{o.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
